// Visitor tracking — fetches IP geolocation from ipapi.co, writes a
// "visit" row to Supabase, then logs every page view.
//
// All calls are fire-and-forget — tracking failures NEVER block the UI.

import { getSupabase } from './SupabaseClient';

const SESSION_KEY = 'gb4k_session_id_v1';
const SESSION_INSERTED_FLAG = 'gb4k_session_inserted_v1';

export interface GeoInfo {
  ip?: string;
  country?: string;
  country_code?: string;
  city?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
}

let cachedGeo: GeoInfo | null = null;
let geoListeners: ((g: GeoInfo) => void)[] = [];

export function onGeoResolved(fn: (g: GeoInfo) => void): () => void {
  if (cachedGeo) fn(cachedGeo);
  geoListeners.push(fn);
  return () => {
    const i = geoListeners.indexOf(fn);
    if (i >= 0) geoListeners.splice(i, 1);
  };
}

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'no-session-' + Date.now();
  }
}

async function fetchGeo(): Promise<GeoInfo> {
  try {
    const res = await fetch('https://ipapi.co/json/', { method: 'GET' });
    if (!res.ok) throw new Error(`ipapi HTTP ${res.status}`);
    const j = await res.json();
    return {
      ip: j.ip,
      country: j.country_name,
      country_code: j.country_code,
      city: j.city,
      region: j.region,
      latitude: j.latitude,
      longitude: j.longitude,
      timezone: j.timezone,
    };
  } catch (err) {
    console.warn('[Tracker] geo fetch failed:', err);
    return {};
  }
}

export async function initTracking(): Promise<void> {
  const supa = getSupabase();
  if (!supa) return;

  const sessionId = getOrCreateSessionId();

  // Avoid double-inserting on hot-reload during dev
  if (sessionStorage.getItem(SESSION_INSERTED_FLAG) === 'true') {
    // Still resolve geo for the listeners (greeting)
    const geo = await fetchGeo();
    cachedGeo = geo;
    geoListeners.forEach((fn) => fn(geo));
    return;
  }

  const geo = await fetchGeo();
  cachedGeo = geo;
  geoListeners.forEach((fn) => fn(geo));

  const payload = {
    session_id: sessionId,
    ip: geo.ip ?? null,
    country: geo.country ?? null,
    country_code: geo.country_code ?? null,
    city: geo.city ?? null,
    region: geo.region ?? null,
    latitude: geo.latitude ?? null,
    longitude: geo.longitude ?? null,
    timezone: geo.timezone ?? null,
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
  };

  const { error } = await supa.from('gb4k_fr_visits').insert(payload);
  if (error) {
    console.warn('[Tracker] visit insert failed:', error.message);
  } else {
    try { sessionStorage.setItem(SESSION_INSERTED_FLAG, 'true'); } catch {}
  }
}

export async function trackPageView(pageId: string): Promise<void> {
  const supa = getSupabase();
  if (!supa) return;
  const sessionId = getOrCreateSessionId();
  const { error } = await supa.from('gb4k_fr_page_views').insert({
    session_id: sessionId,
    page_id: pageId,
  });
  if (error) {
    console.warn('[Tracker] page_view insert failed:', error.message);
  }
}

export function getCachedGeo(): GeoInfo | null {
  return cachedGeo;
}
